// src/utilities/credentialFields.js — SOUND-709 (Track A step 5)
//
// Pure logic behind the credentials surface. Lifted out of the component
// deliberately: sound-app has NO component-test harness (SOUND-679), and four
// defects last sprint were only catchable by a human clicking. Anything that
// can be a pure function and tested should be.
//
// THE PROBLEM THIS SURFACE EXISTS TO FIX. A portal credential has nowhere to
// put a password, so people typed it into the free-text notes box: 7 such
// values in July, 17 by late August, roughly one every two days. The fix is not
// a warning — it is a field.

/** A value is a SECRET if it goes to the vault. Everything else is metadata. */
export const SECRET_FIELDS_BY_KIND = {
  portal: ["password"],
  api: [],      // api secret field names come from the provider registry
  hybrid: ["password"],
};

/**
 * Split a credential's editable inputs into what goes to the vault and what
 * stays in Mongo.
 *
 * `username` STAYS IN MONGO on purpose. It is the same exposure class as
 * loginId, already plaintext, and it has to be LISTABLE: with 19 NMI portal
 * logins across 20 companies, the daily problem is telling them apart. If
 * reading a username costs an audited reveal, the audit log fills with noise
 * and stops being evidence of anything.
 */
export function splitCredentialInput(input = {}, secretFieldNames = []) {
  const secrets = {};
  const metadata = {};
  const secretSet = new Set(secretFieldNames);
  for (const [k, v] of Object.entries(input)) {
    if (v === undefined) continue;
    (secretSet.has(k) ? secrets : metadata)[k] = v;
  }
  return { secrets, metadata };
}

/**
 * Secret field names for a credential, from the DB-backed provider registry
 * plus the kind. Never a hardcoded map: PROVIDER_PRESETS listed three providers
 * while nine were in use, which is how `phoenix: ["apiKey","storeIds"]` ended up
 * treating store IDs — configuration — as a credential field.
 */
export function secretFieldsFor({ kind, provider, providerRegistry = [] } = {}) {
  const reg = providerRegistry.find((p) => p.key === String(provider || "").toLowerCase());
  const fromRegistry = reg?.secretFields || [];
  // A PORTAL always offers a password even if the registry has not recorded one
  // for that provider yet — a portal login with nowhere to put a password is
  // the exact gap this epic exists to close, and a brand-new provider should
  // not reopen it.
  //
  // An API credential offers only what the registry knows, because guessing
  // "password" for a provider that actually wants an apiKey teaches people to
  // put the value under the wrong name.
  const fromKind = kind === "api" ? [] : SECRET_FIELDS_BY_KIND[kind] || [];
  return [...new Set([...fromRegistry, ...fromKind])];
}

/**
 * Does this text look like a secret someone pasted into a notes box?
 *
 * Used to warn on SAVE, not to block it — a note legitimately can contain a
 * short token-ish string, and refusing to save someone's note is how you teach
 * them to put it somewhere worse. Mirrors the detector used to measure the
 * problem: a single token, mixed character classes, credential-ish length.
 */
export function looksLikeSecret(text) {
  const n = String(text || "").trim();
  if (!n || /\s/.test(n)) return false;
  if (n.length < 6 || n.length > 40) return false;
  const classes = [/[a-z]/, /[A-Z]/, /[0-9]/, /[^A-Za-z0-9]/].filter((r) => r.test(n)).length;
  return classes >= 2;
}

/** Has this credential got anywhere to put a secret at all? */
export function hasSecretStorage(cred) {
  return Boolean(cred?.vault && Object.keys(cred.vault.fields || {}).length);
}
